"use client";

import React from "react";
import { useEntityView } from "@/analytics/useEntityView";
import { articlePageUrl } from "@/analytics/content-urls";
import ArticlePageLink from "@/components/ui/ArticlePageLink";
import type { BlockBase } from "@/cms/blocks/registry/types";
import { ImageBlock } from "./ImageBlock";
import { ProductCardBlock } from "./ProductCardBlock";
import { CustomDrawer } from "@/components/ui/CustomDrawer";
import { ChevronDown, Loader2, X } from "lucide-react";
import OverlayWrapper from "@/components/ui/OverLayWrapper";
import { CustomModal } from "@/components/ui/CustomModal";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import arrow1 from "@/../public/assets/images/arrow1.svg";
import arrow2 from "@/../public/assets/images/arrow2.svg";
import {
  TABLE_MAX_COLS,
  TABLE_MAX_ROWS,
  createTableDoc,
  sanitizeTableDoc,
  type TableBlockData,
} from "@/cms/blocks/table";
import Image from "next/image";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { ContactUsSVG, OverlaySVG } from "@/components/ui/SVG";
import ContactConsultationForm from "@/app/[locale]/contact/ContactConsultationForm";
import { SITE_URL } from "@/seo/site";

type Props = {
  blocks: BlockBase[];
  locale: "en" | "fa";
  showBlockNumbers?: boolean;
  sourceMetadata?: Record<string, unknown> | null;
  headingAnchors?: Record<string, string>;
};
type InlineRowItem = {
  kind: "inlineRow";
  media: BlockBase;
  text: BlockBase;
  align: "left" | "right";
  mediaIndex: number;
  textIndex: number;
};
type RenderItem =
  | {
      kind: "block";
      block: BlockBase;
      index: number;
    }
  | InlineRowItem;

const DEFAULT_TABLE_ROW_HEIGHT = 64;

function areNumberArraysEqual(a: number[], b: number[]) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

type FaqImageData = {
  assetId?: string;
  url?: string;
  alt?: string;
  width?: number;
  height?: number;
  previewUrl?: string;
  sourceName?: string;
};

const sentLogKeys = new Set<string>();
const headingStyles: Record<number, string> = {
  1: "lg:text-[26px] text-[20px] leading-9 font-bold px-4 mt-6 mb-2",
  2: "lg:text-[22px] text-[19px] font-bold px-2 mt-8 mb-2 leading-9",
  3: "lg:text-[20px] text-[18px] font-bold px-2 leading-9 mb-2",
};

const CONTENT_TYPE_LABELS: Record<string, string> = {
  ARTICLE: "Article",
  STATIC_PAGE: "Static Page",
  PRODUCT_LIST: "Product List",
};

const CONTENT_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  review: "Review",
  published: "Published",
  archived: "Archived",
};

function formatContentTypeLabel(type?: string | null) {
  if (!type) return "";
  return CONTENT_TYPE_LABELS[type] ?? type.replace(/_/g, " ");
}

function formatContentStatusLabel(status?: string | null) {
  if (!status) return "";
  return CONTENT_STATUS_LABELS[status] ?? status;
}

function pickFirstNonEmptyString(...values: Array<string | null | undefined>) {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return "";
}

function getContentReferenceTitle(data: any, locale: "en" | "fa") {
  return locale === "fa"
    ? pickFirstNonEmptyString(
        data?.title_fa,
        data?.title_en,
        data?.slug_fa,
        data?.slug_en,
        data?.contentId,
      )
    : pickFirstNonEmptyString(
        data?.title_en,
        data?.title_fa,
        data?.slug_en,
        data?.slug_fa,
        data?.contentId,
      );
}

function getContentReferenceSlug(data: any, locale: "en" | "fa") {
  return locale === "fa"
    ? pickFirstNonEmptyString(data?.slug_fa, data?.slug_en)
    : pickFirstNonEmptyString(data?.slug_en, data?.slug_fa);
}

function plainTextFromRich(rich: any): string {
  const parts: string[] = [];
  const walk = (node: any) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (typeof node === "object") {
      if (typeof node.text === "string") {
        parts.push(node.text);
      }
      if (Array.isArray(node.content)) {
        node.content.forEach(walk);
      }
    }
  };
  walk(rich);
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

function isInlineCandidate(block: BlockBase) {
  return (
    block.type === "image" ||
    (block.type === "quote" && (block.data as any)?.variant === "bracket")
  );
}

function isTextBlock(block: BlockBase) {
  return (
    block.type === "paragraph" ||
    block.type === "heading" ||
    block.type === "quote"
  );
}

function FaqImage({
  data,
  locale,
  className,
}: {
  data?: FaqImageData | null;
  locale: "en" | "fa";
  className?: string;
}) {
  const [resolved, setResolved] = React.useState<{
    src: string;
    alt?: string;
  } | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!data) {
      setResolved(null);
      return () => {
        cancelled = true;
      };
    }

    const directSrc =
      (typeof data.url === "string" && data.url.trim()) ||
      (typeof data.previewUrl === "string" && data.previewUrl.trim()) ||
      "";

    if (directSrc) {
      setResolved({ src: directSrc, alt: data.alt });
      return () => {
        cancelled = true;
      };
    }

    if (!data.assetId) {
      setResolved(null);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const response = await fetch("/api/cms/resolve-media", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ ref: data, locale }),
        });
        const payload = await response.json();
        if (!cancelled && payload?.src) {
          setResolved({
            src: payload.src,
            alt: payload.alt || data.alt,
          });
        }
      } catch (error) {
        console.warn("faq resolve-media failed", error);
        if (!cancelled) setResolved(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [data, locale]);

  if (!resolved?.src) {
    return <div className="h-full w-full rounded-lg bg-gray-100" />;
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={resolved.src}
      alt={resolved.alt || ""}
      className={clsx("h-full w-full object-contain", className)}
      loading="lazy"
    />
  );
}

function FaqBlock({
  block,
  locale,
  onContentLinkClick,
}: {
  block: BlockBase;
  locale: "en" | "fa";
  onContentLinkClick?: (slug: string) => void;
}) {
  const [openIndex, setOpenIndex] = React.useState<number | null>(0);
  const items = Array.isArray((block.data as any)?.items)
    ? (block.data as any).items
    : [];
  if (!items.length) {
    return (
      <div className="rounded border bg-gray-50 p-3 text-sm text-gray-600">
        No FAQs yet.
      </div>
    );
  }
  return (
    <div className="px-2">
      <h2 className="relative mx-1 mt-8 mb-2 text-lg font-bold lg:text-[22px]">
        {locale === "fa" ? "سوالات پرتکرار" : "People also asked"}
        <span
          className={`absolute bottom-0.5 -left-4 rtl:-right-3 rtl:left-auto`}
        >
          <Image
            className="w-2.5"
            src={arrow2}
            alt="arrow"
            style={{
              transform: locale === "fa" ? "scaleX(-1)" : "none",
            }}
          />
        </span>
      </h2>

      {items.map((item: any, index: number) => {
        const questionText =
          plainTextFromRich(item?.question?.richText) ||
          item?.question?.text ||
          `Question ${index + 1}`;
        const answerNode = item?.answer?.richText ? (
          renderRichText(item.answer.richText, {
            locale,
            onContentLinkClick,
          })
        ) : (
          <p>{item?.answer?.text ?? ""}</p>
        );
        const imageData = item?.image;
        const imagePosition: "inlineLeft" | "inlineRight" | "top" | "bottom" =
          item?.imagePosition &&
          ["inlineLeft", "inlineRight", "top", "bottom"].includes(
            item.imagePosition,
          )
            ? item.imagePosition
            : locale === "fa"
              ? "inlineLeft"
              : "inlineRight";
        const key = item?.id ?? `faq-${index}`;
        const isOpen = openIndex === index;
        const answerId = `faq-answer-${block.id}-${index}`;
        return (
          <div key={key} className="bg-white">
            <div className="border-b border-gray-300 bg-white py-2.5">
              <button
                type="button"
                className="mx-1 flex w-full items-center justify-between gap-4 text-left text-base font-semibold"
                onClick={() =>
                  setOpenIndex((current) => (current === index ? null : index))
                }
                aria-expanded={isOpen}
                aria-controls={answerId}
              >
                <span className="relative text-[15px] lg:text-base ltr:text-left rtl:text-right rtl:leading-8.25">
                  {questionText}
                  <span className="absolute top-3.5 -right-3.5 rotate-180">
                    <Image className="w-3" src={arrow1} alt="arrow" />
                  </span>
                </span>
                <div className="block self-start pt-1">
                  <ChevronDown
                    size={24}
                    color="var(--color-pinkCustom)"
                    className={`transition-transform duration-200 ${
                      isOpen ? "-rotate-180" : ""
                    }`}
                  />
                </div>
              </button>
              <motion.div
                id={answerId}
                initial={false}
                animate={{
                  opacity: isOpen ? 1 : 0,
                  height: isOpen ? "auto" : 0,
                }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                aria-hidden={!isOpen}
                inert={!isOpen}
                className="overflow-hidden"
              >
                <div className="mt-2 text-sm leading-relaxed after:clear-both after:block after:content-['']">
                  {imageData && imagePosition === "top" ? (
                    <div className="mb-3 w-full">
                      <div className="h-32 w-full overflow-clip rounded-lg">
                        <FaqImage
                          data={imageData}
                          locale={locale}
                          className="h-32 w-full object-contain"
                        />
                      </div>
                    </div>
                  ) : null}

                  {imageData &&
                  (imagePosition === "inlineLeft" ||
                    imagePosition === "inlineRight") ? (
                    <div
                      className={clsx(
                        "mb-3 max-w-[200px] shrink-0",
                        imagePosition === "inlineLeft"
                          ? "float-left mr-4"
                          : "float-right ml-4",
                      )}
                    >
                      <div className="h-32 w-32 rounded-lg">
                        <FaqImage
                          data={imageData}
                          locale={locale}
                          className="object-contain"
                        />
                      </div>
                    </div>
                  ) : null}

                  <div
                    className={clsx(
                      "prose max-w-none text-sm lg:text-base ltr:text-left ltr:leading-8 rtl:text-justify rtl:leading-8.25 rtl:tracking-tight",
                    )}
                    style={
                      locale === "fa" ? { textAlign: "justify" } : undefined
                    }
                  >
                    {answerNode}
                  </div>

                  {imageData && imagePosition === "bottom" ? (
                    <div className="mt-3 w-full">
                      <div className="h-32 w-full overflow-clip rounded-lg">
                        <FaqImage
                          data={imageData}
                          locale={locale}
                          className="h-32 w-full object-contain"
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </motion.div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function getInlineMeta(block: BlockBase): {
  enabled: boolean;
  align: "left" | "right";
} {
  const inline = (block.meta as any)?.inline;
  return {
    enabled: Boolean(inline?.enabled),
    align: inline?.align === "right" ? "right" : "left",
  };
}

function buildRenderItems(blocks: BlockBase[]): RenderItem[] {
  const items: RenderItem[] = [];
  for (let i = 0; i < blocks.length; i += 1) {
    const block = blocks[i];
    const next = blocks[i + 1];
    const inline = getInlineMeta(block);
    if (
      inline.enabled &&
      isInlineCandidate(block) &&
      next &&
      isTextBlock(next)
    ) {
      items.push({
        kind: "inlineRow",
        media: block,
        text: next,
        align: inline.align,
        mediaIndex: i,
        textIndex: i + 1,
      });
      i += 1;
      continue;
    }
    items.push({ kind: "block", block, index: i });
  }
  return items;
}

async function postRuntimeLogOnce(key: string, payload: Record<string, any>) {
  if (sentLogKeys.has(key)) return;
  sentLogKeys.add(key);
  try {
    await fetch("/api/cms/logs/runtime", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.warn("Failed to send runtime log", error);
  }
}

export async function ServerRenderer({
  blocks,
  locale,
  showBlockNumbers,
  sourceMetadata,
  headingAnchors,
}: Props) {
  return (
    <ClientRenderer
      blocks={blocks}
      locale={locale}
      showBlockNumbers={showBlockNumbers}
      sourceMetadata={sourceMetadata}
      headingAnchors={headingAnchors}
    />
  );
}

export function ClientRenderer({
  blocks,
  locale,
  showBlockNumbers = false,
  sourceMetadata = null,
  headingAnchors,
}: Props) {
  const dir = locale === "fa" ? "rtl" : "ltr";
  const alignment = locale === "fa" ? "text-right" : "text-left";
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [previewSlug, setPreviewSlug] = React.useState<string | null>(null);
  const [previewContent, setPreviewContent] = React.useState<any | null>(null);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const previewRequest = React.useRef<AbortController | null>(null);
  React.useEffect(() => () => previewRequest.current?.abort(), []);
  const articleUrl = articlePageUrl(previewContent, locale, previewSlug);
  useEntityView({
    type: "content",
    id: previewContent?.id,
    url: articleUrl,
    open: Boolean(
      previewSlug && previewContent && !previewLoading && !previewError,
    ),
  });
  const referenceIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          (blocks ?? [])
            .filter((b) => b.type === "reference")
            .flatMap((b) => {
              const data = (b.data as any) ?? {};
              const ids = Array.isArray(data.referenceIds)
                ? data.referenceIds
                : data.referenceId
                  ? [data.referenceId]
                  : [];
              return ids.filter(
                (id: any) => typeof id === "string" && Boolean(id.trim()),
              );
            })
            .filter(
              (id): id is string =>
                typeof id === "string" && Boolean(id.trim()),
            ),
        ),
      ),
    [blocks],
  );
  const [referenceMap, setReferenceMap] = React.useState<Map<string, any>>(
    new Map(),
  );
  const renderItems = React.useMemo(
    () => buildRenderItems(blocks ?? []),
    [blocks],
  );

  React.useEffect(() => {
    let cancelled = false;
    if (!referenceIds.length) {
      setReferenceMap(new Map());
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `/api/cms/references/resolve?ids=${encodeURIComponent(referenceIds.join(","))}`,
        );
        const data = await res.json();
        if (!res.ok)
          throw new Error(data?.message || "Failed to load references");
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        setReferenceMap(new Map(items.map((item: any) => [item.id, item])));
      } catch (error) {
        console.warn("Failed to load references", error);
        if (!cancelled) {
          setReferenceMap(new Map());
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [referenceIds]);

  const handleContentLinkClick = React.useCallback(
    async (slug: string) => {
      const normalized = (slug ?? "").trim();
      if (!normalized) return;
      previewRequest.current?.abort();
      const controller = new AbortController();
      previewRequest.current = controller;
      setPreviewContent(null);
      setPreviewSlug(normalized);
      setPreviewError(null);
      setPreviewLoading(true);
      try {
        const res = await fetch(
          `/api/cms/content/by-slug?slug=${encodeURIComponent(normalized)}&locale=${locale}`,
          { cache: "no-store", signal: controller.signal },
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(
            data?.message || data?.error || "Failed to load content",
          );
        }
        if (!controller.signal.aborted)
          setPreviewContent(data?.content ?? null);
      } catch (error: any) {
        if (controller.signal.aborted) return;
        setPreviewContent(null);
        setPreviewError(error?.message || "Unable to load content");
      } finally {
        if (!controller.signal.aborted) setPreviewLoading(false);
      }
    },
    [locale],
  );

  const closePreview = React.useCallback(() => {
    previewRequest.current?.abort();
    setPreviewSlug(null);
    setPreviewContent(null);
    setPreviewError(null);
    setPreviewLoading(false);
  }, []);

  const renderBlockWithIndex = React.useCallback(
    (block: BlockBase, index: number) => (
      <div className={showBlockNumbers ? "relative" : undefined}>
        {showBlockNumbers ? (
          <span className="pointer-events-none absolute -top-2 -right-1 z-0 rounded text-[10px] font-semibold text-gray-700">
            {index + 1}
          </span>
        ) : null}
        <Block
          block={block}
          locale={locale}
          referenceMap={referenceMap}
          onContentLinkClick={handleContentLinkClick}
          sourceMetadata={sourceMetadata}
          headingAnchors={headingAnchors}
        />
      </div>
    ),
    [
      handleContentLinkClick,
      headingAnchors,
      locale,
      referenceMap,
      showBlockNumbers,
      sourceMetadata,
    ],
  );

  return (
    <>
      <div className={`${alignment}`} dir={dir}>
        {renderItems.map((item) =>
          item.kind === "inlineRow" ? (
            <div
              key={`inline-${item.media.id}-${item.text.id}`}
              className="md:after:clear-both md:after:block md:after:content-['']"
            >
              <div
                className={`mb-4 w-full md:mb-2 md:w-[45%] ${
                  item.align === "right"
                    ? "md:float-right md:ml-4"
                    : "md:float-left md:mr-4"
                }`}
              >
                {renderBlockWithIndex(item.media, item.mediaIndex)}
              </div>
              {renderBlockWithIndex(item.text, item.textIndex)}
            </div>
          ) : (
            <React.Fragment key={item.block.id}>
              {renderBlockWithIndex(item.block, item.index)}
            </React.Fragment>
          ),
        )}
      </div>

      <AnimatePresence>
        {isDesktop ? (
          <CustomDrawer
            open={Boolean(previewSlug)}
            onOpenChange={(v) => {
              if (!v) closePreview();
            }}
            side="right"
            direction={dir}
            widthClass="w-full md:max-w-[85vw] xl:max-w-[70vw]"
            bodyClassName="p-0"
            className="overflow-hidden"
            header={
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    {previewContent
                      ? pickFirstNonEmptyString(
                          locale === "fa"
                            ? previewContent.title_fa
                            : previewContent.title_en,
                          locale === "fa"
                            ? previewContent.slug_fa
                            : previewContent.slug_en,
                          previewSlug ?? "",
                        )
                      : (previewSlug ?? "Content")}
                  </div>
                </div>
                <ArticlePageLink
                  href={!previewLoading ? articleUrl : null}
                  locale={locale}
                  className="ms-auto"
                />
                <button
                  type="button"
                  aria-label={locale === "fa" ? "???? ?????" : "Close article"}
                  className="bg-grayCustom text-slateCustom hover:text-pinkCustom flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition"
                  onClick={closePreview}
                >
                  <X className="h-4 w-4" strokeWidth={4} />
                </button>
              </div>
            }
          >
            <div className="min-w-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              {previewLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading content…
                </div>
              ) : previewError ? (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {previewError}
                </div>
              ) : previewContent ? (
                <ClientRenderer
                  blocks={
                    locale === "fa"
                      ? ((previewContent.blocks_fa as BlockBase[]) ?? [])
                      : ((previewContent.blocks_en as BlockBase[]) ?? [])
                  }
                  locale={locale}
                />
              ) : (
                <div className="text-sm text-gray-600">No content to show.</div>
              )}
            </div>
          </CustomDrawer>
        ) : previewSlug ? (
          <OverlayWrapper
            onClose={closePreview}
            snapPoints={["92vh", "98vh"]}
            initialSnap={1}
            dismissOnOverlay
            draggable={false}
          >
            <div
              dir={dir}
              className="flex h-full flex-col overflow-hidden rounded-t-2xl bg-white"
            >
              <div className="bg-grayCustom grid grid-cols-6 items-center justify-between border-b border-gray-200 px-4 py-3">
                <div className="col-span-4 col-start-2 min-w-0 text-center">
                  <div className="text-sm font-semibold text-gray-900">
                    {previewContent
                      ? pickFirstNonEmptyString(
                          locale === "fa"
                            ? previewContent.title_fa
                            : previewContent.title_en,
                          locale === "fa"
                            ? previewContent.slug_fa
                            : previewContent.slug_en,
                          previewSlug ?? "",
                        )
                      : (previewSlug ?? "Content")}
                  </div>
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  className="bg-grayCustom text-slateCustom hover:text-pinkCustom col-span-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition"
                  onClick={closePreview}
                >
                  <X className="h-4 w-4" strokeWidth={4} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <ArticlePageLink
                  href={!previewLoading ? articleUrl : null}
                  locale={locale}
                  className="mb-4 w-full"
                />
                {previewLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading content…
                  </div>
                ) : previewError ? (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    {previewError}
                  </div>
                ) : previewContent ? (
                  <ClientRenderer
                    blocks={
                      locale === "fa"
                        ? ((previewContent.blocks_fa as BlockBase[]) ?? [])
                        : ((previewContent.blocks_en as BlockBase[]) ?? [])
                    }
                    locale={locale}
                  />
                ) : (
                  <div className="text-sm text-gray-600">
                    No content to show.
                  </div>
                )}
              </div>
            </div>
          </OverlayWrapper>
        ) : null}
      </AnimatePresence>
    </>
  );
}

function ContactBlockCta({
  locale,
  sourceMetadata,
}: {
  locale: "en" | "fa";
  sourceMetadata?: Record<string, unknown> | null;
}) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [open, setOpen] = React.useState(false);
  const copy =
    locale === "fa"
      ? {
          title: "ارتباط سریع با فربین",
          description:
            "برای دریافت مشاوره یا پاسخ سریع، درخواست خود را ثبت کنید.",
          modalTitle: "درخواست تماس و مشاوره",
        }
      : {
          title: "Contact Farbin",
          description:
            "Send your question or request a callback from our team.",
          modalTitle: "Request consultation and call",
        };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-6 flex w-full cursor-pointer flex-col items-center gap-4 rounded-2xl from-white to-pink-50 px-4 py-4 text-center transition"
      >
        <ContactUsSVG
          className="h-12 w-12"
          accentColor="var(--color-gray-300)"
          svgColor="pinkCustom"
        />
        <div className="min-w-0">
          <div className="font-semibold text-slate-900">{copy.title}</div>
          <div className="text-sm text-slate-600">{copy.description}</div>
        </div>
      </button>

      {isDesktop ? (
        <CustomModal
          open={open}
          onOpenChange={setOpen}
          maxWidth="2xl"
          bodyClassName="p-6"
        >
          <div className="mb-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-full border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <ContactConsultationForm
            locale={locale}
            title={copy.modalTitle}
            source="article_support"
            metadata={sourceMetadata ?? null}
          />
        </CustomModal>
      ) : (
        <AnimatePresence>
          {open ? (
            <OverlayWrapper
              onClose={() => setOpen(false)}
              snapPoints={["82vh"]}
              initialSnap={1}
              dismissOnOverlay
              draggable={false}
            >
              <div className="flex h-full flex-col overflow-hidden rounded-t-2xl bg-white">
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-full border border-gray-200 p-2 text-gray-500"
                    aria-label="Close"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 overflow-auto px-4 py-4">
                  <ContactConsultationForm
                    locale={locale}
                    title={copy.modalTitle}
                    source="article_support"
                    metadata={sourceMetadata ?? null}
                  />
                </div>
              </div>
            </OverlayWrapper>
          ) : null}
        </AnimatePresence>
      )}
    </>
  );
}

function Block({
  block,
  locale,
  referenceMap,
  onContentLinkClick,
  sourceMetadata,
  headingAnchors,
}: {
  block: BlockBase;
  locale: "en" | "fa";
  referenceMap: Map<string, any>;
  onContentLinkClick?: (slug: string) => void;
  sourceMetadata?: Record<string, unknown> | null;
  headingAnchors?: Record<string, string>;
}) {
  switch (block.type) {
    case "contact": {
      return (
        <ContactBlockCta locale={locale} sourceMetadata={sourceMetadata} />
      );
    }
    case "heading": {
      const { level, text } = block.data as any;
      const safeLevel = Math.min(Math.max(Number(level) || 2, 1), 3);
      const className = headingStyles[safeLevel] ?? headingStyles[2];
      const anchorId = headingAnchors?.[block.id];

      switch (safeLevel) {
        case 1:
          return (
            <h1 className={clsx(className, "relative")}>
              {text}
              <span
                className={`absolute top-5.5 left-0 rtl:right-0 rtl:left-auto`}
              >
                <Image
                  className="w-3.5"
                  src={arrow2}
                  alt="arrow"
                  style={{
                    transform: locale === "fa" ? "scaleX(-1)" : "none",
                  }}
                />
              </span>
            </h1>
          );
        case 2:
          return (
            <h2
              id={anchorId}
              className={clsx(className, anchorId && "scroll-mt-28")}
            >
              {text}
            </h2>
          );
        case 3:
          return <h3 className={className}>{text}</h3>;
        default:
          return <h2 className={className}>{text}</h2>;
      }
    }
    case "paragraph": {
      const d = block.data as any;
      if (d.richText) {
        const textContent = renderRichText(d.richText, {
          locale,
          onContentLinkClick,
        });
        return (
          <p className="mb-6 px-2 lg:text-lg ltr:leading-8 rtl:text-justify rtl:leading-8.5 rtl:tracking-tight">
            {textContent}
          </p>
        );
      }
      return (
        <p className="mb-6 px-2 lg:text-lg ltr:leading-8 rtl:text-justify rtl:leading-8.5 rtl:tracking-tight">
          {d.text}
        </p>
      );
    }
    case "image": {
      return <ImageBlock data={block.data as any} locale={locale} />;
    }
    case "divider":
      return (block.data as any)?.variant === "space" ? (
        <div className="h-12" />
      ) : (
        <hr />
      );
    case "list": {
      const { ordered, items } = block.data as any;
      const ListTag = ordered ? "ol" : "ul";
      const listClasses = [
        "list-inside",
        ordered ? "list-decimal" : "list-disc",
        "space-y-1",
        locale === "fa" ? "pr-4" : "pl-4",
        "ltr:leading-8",
        "mb-6",
        "rtl:text-justify",
        "rtl:leading-8.5",
        "rtl:tracking-tight",
        "lg:text-lg",
      ].join(" ");
      return React.createElement(
        ListTag,
        { className: listClasses },
        items?.map((it: string, i: number) => <li key={i}>{it}</li>),
      );
    }
    case "faq": {
      return (
        <FaqBlock
          block={block}
          locale={locale}
          onContentLinkClick={onContentLinkClick}
        />
      );
    }
    case "quote": {
      const { text, cite, variant } = block.data as any;
      if (variant === "bracket") {
        return (
          <div className="mb-6 flex items-stretch gap-2 px-2 ltr:leading-8 rtl:text-justify rtl:leading-9 rtl:tracking-tight">
            <div className="flex w-2 flex-col self-stretch text-current">
              <span className="border-pinkCustom h-2 border-t-2 border-l-2 ltr:border-l-2 rtl:border-r-2 rtl:border-l-0" />
              <span className="border-pinkCustom flex-1 border-l-2 ltr:border-l-2 rtl:border-r-2 rtl:border-l-0" />
              <span className="border-pinkCustom h-2 border-b-2 border-l-2 ltr:border-l-2 rtl:border-r-2 rtl:border-l-0" />
            </div>
            <div className="min-w-0 flex-1 space-y-2 text-gray-700">
              <p>{text}</p>
              {cite ? (
                <footer className="text-sm opacity-70">— {cite}</footer>
              ) : null}
            </div>
            <div className="flex w-2 flex-col self-stretch text-current">
              <span className="border-pinkCustom h-2 border-t-2 border-r-2 ltr:border-l-2 rtl:border-r-0 rtl:border-l-2" />
              <span className="border-pinkCustom flex-1 border-r-2 ltr:border-l-2 rtl:border-r-0 rtl:border-l-2" />
              <span className="border-pinkCustom h-2 border-r-2 border-b-2 ltr:border-l-2 rtl:border-r-0 rtl:border-l-2" />
            </div>
          </div>
        );
      }
      return (
        <blockquote className="border-l-4 px-2 pl-3 italic">
          {text}
          {cite ? (
            <footer className="mt-1 text-sm opacity-70">— {cite}</footer>
          ) : null}
        </blockquote>
      );
    }
    case "link": {
      const data = block.data as any;
      const variant: "default" | "cta" =
        data?.variant === "cta" ? "cta" : "default";
      const mode =
        variant === "cta"
          ? "url"
          : data?.mode === "content"
            ? "content"
            : "url";
      const title = data?.title ?? "";
      if (mode === "content") {
        const slug = data?.contentSlug ?? "";
        const href =
          typeof slug === "string" && slug.trim()
            ? slug.startsWith("/")
              ? `${SITE_URL}/${locale}/c${slug}`
              : `${SITE_URL}/${locale}/c/${slug}`
            : "#";
        const handleClick = (event: React.MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          if (slug) onContentLinkClick?.(slug);
        };
        return (
          <a
            href={href}
            className="text-slateCustom -mt-2 mb-6 flex items-center gap-2 px-2 font-semibold"
            onClick={handleClick}
          >
            <OverlaySVG className="h-4.5 w-4.5" />
            {title || slug || "Content link"}
          </a>
        );
      }
      const href = data?.url ?? "#";
      const target = href.startsWith("/") ? "_self" : "_blank";
      if (variant === "cta") {
        return (
          <a
            href={href}
            target={target}
            rel={target === "_blank" ? "noopener noreferrer" : undefined}
            className="bg-pinkCustom inline-block rounded px-4 py-2 font-semibold text-white shadow-sm"
          >
            {title || href || "CTA"}
          </a>
        );
      }
      return (
        <a
          href={href}
          target={target}
          rel={target === "_blank" ? "noopener noreferrer" : undefined}
          className="text-pinkCustom underline underline-offset-2"
        >
          {title || href || "Link"}
        </a>
      );
    }
    case "embed": {
      const { url } = block.data as any;
      return (
        <div className="aspect-video overflow-clip rounded-xl">
          <iframe
            src={url}
            title="Embedded content"
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
    case "productCard": {
      return <ProductCardBlock data={block.data} />;
    }
    case "contentReference": {
      const data = block.data as any;
      const title = getContentReferenceTitle(data, locale);
      const slug = getContentReferenceSlug(data, locale);
      const typeLabel = formatContentTypeLabel(data?.type);
      const statusLabel = formatContentStatusLabel(data?.status);
      return (
        <div
          data-content-id={data?.contentId ?? undefined}
          className="rounded border bg-gray-50 p-3"
        >
          <div className="text-xs uppercase opacity-60">
            {typeLabel || "Content"}
            {statusLabel ? ` · ${statusLabel}` : ""}
          </div>
          <div className="font-semibold">
            {title || data?.contentId || "No content selected"}
          </div>
          {slug ? (
            <div className="truncate text-xs text-gray-500">Slug: {slug}</div>
          ) : null}
        </div>
      );
    }
    case "reference": {
      const data = (block.data as any) ?? {};
      const referenceIds = Array.isArray(data.referenceIds)
        ? data.referenceIds
        : data.referenceId
          ? [data.referenceId]
          : [];
      if (!referenceIds.length) {
        return (
          <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-500">
            {locale === "fa"
              ? "هیچ مرجعی انتخاب نشده است."
              : "No references selected."}
          </div>
        );
      }
      return (
        <div className="px-2">
          <h3 className="relative text-2xl font-bold">
            {locale === "fa" ? "منابع" : "References"}
            <span
              className={`absolute bottom-0.5 -left-4 rtl:-right-3 rtl:left-auto`}
            >
              <Image
                className="w-2.5"
                src={arrow2}
                alt="arrow"
                style={{
                  transform: locale === "fa" ? "scaleX(-1)" : "none",
                }}
              />
            </span>
          </h3>
          <div className="space-y-6">
            {referenceIds.map((refId: string) => {
              const ref = referenceMap.get(refId);
              if (!ref) {
                return (
                  <div
                    key={refId}
                    className="rounded border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-500"
                  >
                    {locale === "fa"
                      ? "مرجع پیدا نشد."
                      : "Reference not found."}
                  </div>
                );
              }
              const title = ref.name;
              const localizedDescription =
                locale === "fa" ? ref.descriptionFa : ref.descriptionEn;
              const localizedLinkLabel =
                locale === "fa" ? ref.linkLabelFa : ref.linkLabelEn;
              const localizedLinkHref =
                locale === "fa" ? ref.linkHrefFa : ref.linkHrefEn;
              const descriptionText =
                typeof localizedDescription === "string"
                  ? localizedDescription
                  : localizedDescription?.text;
              const descriptionNode = localizedDescription?.richText ? (
                renderRichText(localizedDescription.richText, {
                  locale,
                  onContentLinkClick,
                })
              ) : descriptionText ? (
                <p className="leading-relaxed">{descriptionText}</p>
              ) : null;
              return (
                <div key={refId} className="flex gap-6">
                  {ref.coverAsset?.url ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={ref.coverAsset.url}
                      alt={ref.coverAlt || ""}
                      className="w-40 rounded border border-gray-200 object-cover"
                    />
                  ) : null}
                  <div className="mt-1 flex items-start gap-3">
                    {ref.logoAsset?.url ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={ref.logoAsset.url}
                        alt={ref.logoAlt || ""}
                        className="h-12 w-12 rounded border border-gray-200 object-contain"
                      />
                    ) : null}
                    <div className="flex flex-col gap-6">
                      <div className="text-base font-semibold">
                        {title || ref.slug_en || ref.slug_fa || refId}
                      </div>
                      {descriptionNode ? (
                        <div className="prose max-w-none text-sm leading-relaxed">
                          {descriptionNode}
                        </div>
                      ) : null}
                      {localizedLinkHref ? (
                        <a
                          href={localizedLinkHref}
                          target={
                            localizedLinkHref.startsWith("/")
                              ? "_self"
                              : "_blank"
                          }
                          rel="noreferrer"
                          className="text-pinkCustom text-xs underline underline-offset-2"
                        >
                          {localizedLinkLabel || localizedLinkHref}
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    case "table": {
      return renderTableBlock(block.data as TableBlockData);
    }
    default: {
      const key = `${block.type}:${block.schemaVersion ?? "na"}:${block.id}`;
      postRuntimeLogOnce(key, {
        message: "Unknown block type at render",
        blockId: block.id,
        blockType: block.type,
        schemaVersion: block.schemaVersion ?? null,
        context: { locale },
      });
      return (
        <div className="text-sm text-red-600">Unknown block: {block.type}</div>
      );
    }
  }
}

type RenderedTableCell = {
  text: string;
  isHeader: boolean;
  colspan: number;
  rowspan: number;
  bold: boolean;
  italic: boolean;
  align: "left" | "center" | "right";
  direction: "ltr" | "rtl";
  width?: number;
};

function extractTableCells(doc: any): RenderedTableCell[][] {
  const findTable = (node: any): any | null => {
    if (!node) return null;
    if (node.type === "table") return node;
    if (Array.isArray(node.content)) {
      for (const child of node.content) {
        const found = findTable(child);
        if (found) return found;
      }
    }
    return null;
  };

  const table = findTable(doc);
  if (!table?.content) return [];

  return (table.content as any[]).map((row: any) => {
    const cells = Array.isArray(row?.content) ? row.content : [];
    return cells.map((cell: any) => {
      const paras = Array.isArray(cell?.content) ? cell.content : [];
      const parts: string[] = [];
      paras.forEach((para: any) => {
        if (Array.isArray(para?.content)) {
          para.content.forEach((leaf: any) => {
            if (leaf?.type === "text" && typeof leaf.text === "string") {
              parts.push(leaf.text);
            }
          });
        }
      });
      return {
        text:
          typeof cell?.attrs?.computed === "string"
            ? cell.attrs.computed
            : parts.join("\n").trim(),
        isHeader: cell?.type === "tableHeader",
        colspan:
          Number.isInteger(cell?.attrs?.colspan) && cell.attrs.colspan > 0
            ? cell.attrs.colspan
            : 1,
        rowspan:
          Number.isInteger(cell?.attrs?.rowspan) && cell.attrs.rowspan > 0
            ? cell.attrs.rowspan
            : 1,
        bold: cell?.attrs?.bold === true,
        italic: cell?.attrs?.italic === true,
        align:
          cell?.attrs?.textAlign === "center" ||
          cell?.attrs?.textAlign === "right"
            ? cell.attrs.textAlign
            : "left",
        direction: cell?.attrs?.direction === "rtl" ? "rtl" : "ltr",
        width:
          Array.isArray(cell?.attrs?.colwidth) &&
          typeof cell.attrs.colwidth[0] === "number"
            ? cell.attrs.colwidth[0]
            : undefined,
      };
    });
  });
}

function RenderedTableBlock({
  caption,
  compact,
  fixedCol,
  fixedRow,
  paddingClass,
  rows,
  tableWidth,
}: {
  caption?: string | null;
  compact: boolean;
  fixedCol?: number;
  fixedRow?: number;
  paddingClass: string;
  rows: RenderedTableCell[][];
  tableWidth: number;
}) {
  const rowRefs = React.useRef<Array<HTMLTableRowElement | null>>([]);
  const [rowHeights, setRowHeights] = React.useState<number[]>([]);

  const columnOffsets =
    rows[0]?.map((_, colIdx) =>
      rows[0]
        .slice(0, colIdx)
        .reduce((sum, cell) => sum + (cell.width ?? 180), 0),
    ) ?? [];

  const measureRowHeights = React.useCallback(() => {
    const nextHeights = rows.map(
      (_, rowIdx) =>
        rowRefs.current[rowIdx]?.offsetHeight ?? DEFAULT_TABLE_ROW_HEIGHT,
    );
    setRowHeights((current) =>
      areNumberArraysEqual(current, nextHeights) ? current : nextHeights,
    );
  }, [rows]);

  React.useLayoutEffect(() => {
    measureRowHeights();
  }, [measureRowHeights, rows, tableWidth]);

  React.useEffect(() => {
    window.addEventListener("resize", measureRowHeights);
    return () => window.removeEventListener("resize", measureRowHeights);
  }, [measureRowHeights]);

  const getRowStickyOffset = React.useCallback(
    (rowIdx: number) =>
      rowHeights.slice(0, rowIdx).reduce((sum, height) => sum + height, 0),
    [rowHeights],
  );

  return (
    <div className="max-h-[70vh] overflow-auto">
      <table
        className={`border-collapse text-left ${compact ? "text-xs" : "text-sm"}`}
        style={{
          minWidth: "100%",
          tableLayout: "fixed",
          width: `${tableWidth}px`,
        }}
      >
        <tbody>
          {rows.map((cells, rowIdx) => (
            <tr
              key={`tr-${rowIdx}`}
              ref={(node) => {
                rowRefs.current[rowIdx] = node;
              }}
            >
              {cells.map((cell, cellIdx) => {
                const CellTag = cell.isHeader ? "th" : "td";
                const isFrozenRow = fixedRow !== undefined && rowIdx < fixedRow;
                const isFrozenCol =
                  fixedCol !== undefined && cellIdx < fixedCol;
                const isFreezeRowBoundary =
                  fixedRow !== undefined &&
                  fixedRow > 0 &&
                  rowIdx === fixedRow - 1;
                const isFreezeColBoundary =
                  fixedCol !== undefined &&
                  fixedCol > 0 &&
                  cellIdx === fixedCol - 1;
                const width = cell.width ?? 180;
                const cellStyle: React.CSSProperties = {
                  ...(isFreezeRowBoundary
                    ? { borderBottom: "3px solid rgb(34 197 94)" }
                    : {}),
                  ...(isFreezeColBoundary
                    ? { borderInlineEnd: "3px solid rgb(34 197 94)" }
                    : {}),
                  direction: cell.direction,
                  height: "4rem",
                  minHeight: "4rem",
                  minWidth: `${width}px`,
                  textAlign: cell.align,
                  width: `${width}px`,
                  ...(isFrozenCol
                    ? { insetInlineStart: `${columnOffsets[cellIdx] ?? 0}px` }
                    : {}),
                  ...(isFrozenRow
                    ? { top: `${getRowStickyOffset(rowIdx)}px` }
                    : {}),
                };
                return (
                  <CellTag
                    key={`tc-${rowIdx}-${cellIdx}`}
                    colSpan={cell.colspan}
                    rowSpan={cell.rowspan}
                    className={`wrap-break-words relative border border-gray-200 whitespace-pre-wrap ${paddingClass} ${
                      cell.isHeader || isFrozenRow || isFrozenCol
                        ? "bg-gray-50"
                        : "bg-white"
                    } ${cell.isHeader || cell.bold ? "font-semibold" : ""} ${
                      cell.italic ? "italic" : ""
                    } ${isFrozenRow ? "sticky z-20" : ""} ${
                      isFrozenCol ? "sticky z-10" : ""
                    } ${isFrozenRow && isFrozenCol ? "z-30" : ""}`}
                    style={cellStyle}
                  >
                    {cell.text || "\u00a0"}
                  </CellTag>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {caption ? (
        <div className="mt-2 text-sm text-gray-600">{caption}</div>
      ) : null}
    </div>
  );
}

function renderTableBlock(data: TableBlockData) {
  const compact = data?.variant === "compact";
  const paddingClass = compact ? "px-2 py-1" : "px-3 py-2";
  try {
    const { doc, stats } = sanitizeTableDoc(data?.doc ?? createTableDoc(), {
      maxRows: TABLE_MAX_ROWS,
      maxCols: TABLE_MAX_COLS,
    });
    const rows = extractTableCells(doc);
    if (!rows.length) {
      return null;
    }
    const tableWidth = Math.max(
      rows[0]?.reduce((sum, cell) => sum + (cell.width ?? 180), 0) ?? 0,
      180,
    );
    const fixedRow =
      typeof data?.fixedRow === "number" &&
      Number.isInteger(data.fixedRow) &&
      data.fixedRow >= 0 &&
      data.fixedRow < stats.rows
        ? data.fixedRow
        : undefined;
    const fixedCol =
      typeof data?.fixedCol === "number" &&
      Number.isInteger(data.fixedCol) &&
      data.fixedCol >= 0 &&
      data.fixedCol < stats.cols
        ? data.fixedCol
        : undefined;
    return (
      <RenderedTableBlock
        caption={data?.caption}
        compact={compact}
        fixedCol={fixedCol}
        fixedRow={fixedRow}
        paddingClass={paddingClass}
        rows={rows}
        tableWidth={tableWidth}
      />
    );
  } catch (error) {
    console.log(error);
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        Unable to render this table.
      </div>
    );
  }
}

type RichNode = {
  type?: string;
  text?: string;
  attrs?: Record<string, any>;
  marks?: Array<{ type: string; attrs?: Record<string, any> }>;
  content?: RichNode[];
};

type RenderOptions = {
  locale: "en" | "fa";
  onContentLinkClick?: (slug: string) => void;
  inline?: boolean;
};

export function renderRichText(
  rich: RichNode,
  { onContentLinkClick, inline = false }: RenderOptions,
): React.ReactNode {
  let keySeq = 0;
  const nextKey = () => `r-${keySeq++}`;

  const splitContentLinkText = (value: string) => {
    const match = value.match(/^(\s*)(\S+)([\s\S]*)$/);
    if (!match) {
      return { leadingWhitespace: value, firstWord: "", trailingText: "" };
    }

    return {
      leadingWhitespace: match[1] ?? "",
      firstWord: match[2] ?? "",
      trailingText: match[3] ?? "",
    };
  };

  const renderContentLinkLabel = (value: React.ReactNode) => {
    if (typeof value !== "string") return value;

    const { leadingWhitespace, firstWord, trailingText } =
      splitContentLinkText(value);

    if (!firstWord) return value;

    return (
      <>
        {leadingWhitespace}
        <span className="relative inline-block">
          <span>{firstWord}</span>
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 left-0 flex flex-col gap-[2px]"
          >
            <span className="bg-slateCustom h-px w-4" />
            <span className="bg-slateCustom h-px w-3" />
          </span>
        </span>
        {trailingText}
      </>
    );
  };

  const applyMarks = (
    children: React.ReactNode,
    marks?: Array<{ type: string; attrs?: Record<string, any> }>,
  ) => {
    if (!marks || marks.length === 0) return children;
    return marks.reduce((acc, mark) => {
      switch (mark.type) {
        case "bold":
          return <strong key={nextKey()}>{acc}</strong>;
        case "italic":
          return <em key={nextKey()}>{acc}</em>;
        case "strike":
          return <s key={nextKey()}>{acc}</s>;
        case "code":
          return (
            <code key={nextKey()} className="rounded bg-gray-100 px-1">
              {acc}
            </code>
          );
        case "underline":
          return <u key={nextKey()}>{acc}</u>;
        case "contentLink": {
          const slug =
            mark.attrs?.contentSlug ??
            mark.attrs?.["data-content-slug"] ??
            mark.attrs?.slug ??
            "";
          const onClick = (event: React.MouseEvent | React.KeyboardEvent) => {
            event.preventDefault();
            event.stopPropagation();
            if (!slug) return;
            onContentLinkClick?.(slug);
          };
          return (
            <span
              key={nextKey()}
              role={onContentLinkClick ? "button" : undefined}
              tabIndex={onContentLinkClick ? 0 : undefined}
              onClick={onClick}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  onClick(event);
                }
              }}
              className="cursor-pointer text-gray-900"
              data-content-slug={slug}
            >
              {renderContentLinkLabel(acc)}
            </span>
          );
        }
        case "link": {
          const href = mark.attrs?.href ?? "#";
          const target =
            mark.attrs?.target ?? (href.startsWith("/") ? "_self" : "_blank");
          return (
            <a
              key={nextKey()}
              href={href}
              target={target}
              rel={target === "_blank" ? "noopener noreferrer" : undefined}
              className="underline underline-offset-2"
            >
              {acc}
            </a>
          );
        }
        default:
          return acc;
      }
    }, children);
  };

  const renderChildren = (nodes?: RichNode[]) =>
    (nodes ?? []).map((n) => renderNode(n));

  const renderNode = (node: RichNode): React.ReactNode => {
    const k = nextKey();
    switch (node.type) {
      case "doc":
        return <>{renderChildren(node.content)}</>;

      case "paragraph": {
        const children = renderChildren(node.content);
        const isEmptyParagraph = !node.content || node.content.length === 0;

        // Inline rendering
        if (inline) {
          return (
            <React.Fragment key={k}>
              {isEmptyParagraph ? "\u00A0" : children}
              <span className="block h-4" />
            </React.Fragment>
          );
        }

        if (isEmptyParagraph) {
          return <div key={k} className={`h-5.5`} />;
        }

        return (
          <p key={k} className={`mb-1 last:mb-0`}>
            {children}
          </p>
        );
      }

      case "text": {
        const inner = node.text ?? "";
        return (
          <React.Fragment key={k}>
            {applyMarks(inner, node.marks)}
          </React.Fragment>
        );
      }

      case "hardBreak":
        return <br key={k} />;

      case "horizontalRule":
        return <hr key={k} />;

      case "codeBlock":
        return (
          <pre key={k} className="overflow-x-auto rounded bg-gray-100 p-3">
            <code>
              {(node.content ?? []).map((c) => (c.text ? c.text : ""))}
            </code>
          </pre>
        );

      default:
        // Fallback: render children inline
        return <span key={k}>{renderChildren(node.content)}</span>;
    }
  };

  return renderNode(rich);
}
